import { useEffect, useMemo, useState, useDeferredValue } from "react";
import axios from "axios";
import { ChevronLeft, Search, Loader2, Wallet, RefreshCw, Eye } from "lucide-react";
import { toast } from "sonner";
import { smartMatch, normAr } from "../../utils/arabicSearch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtDay = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { year: "2-digit", month: "2-digit", day: "2-digit" }); }
  catch { return "—"; }
};
const digits = (s) => String(s || "").replace(/\D/g, "");

const matches = (c, q) => {
  const qd = digits(q);
  if (qd.length >= 3 && (digits(c.phone).includes(qd) || digits(c.customer_code).includes(qd))) return true;
  const hay = `${c.full_name} ${c.market_name} ${c.area}`;
  return normAr(hay).includes(normAr(q.trim())) || smartMatch(hay, q);
};

const CustomerBalances = ({ onBack }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const dq = useDeferredValue(q);
  const [onlyDebt, setOnlyDebt] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/supervisor/customer-balances`);
      setRows(data.customers || []);
    } catch { toast.error("فشل تحميل الأرصدة"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const list = useMemo(() => {
    let r = onlyDebt ? rows.filter((c) => c.debt > 0) : rows;
    if (dq.trim()) r = r.filter((c) => matches(c, dq));
    return r;
  }, [rows, dq, onlyDebt]);
  const total = list.reduce((s, c) => s + Math.max(0, c.debt), 0);

  return (
    <div className="space-y-3" dir="rtl" data-testid="sup-balances">
      <div className="flex items-center justify-between">
        <button type="button" onClick={onBack} data-testid="sup-balances-back"
          className="flex items-center gap-1 text-sm font-bold text-[#1E3A5F]">
          <ChevronLeft className="w-4 h-4 rotate-180" /> رجوع
        </button>
        <span className="text-[10px] font-bold bg-slate-100 text-slate-600 rounded-full px-2 py-1 flex items-center gap-1">
          <Eye className="w-3 h-3" /> عرض فقط
        </span>
      </div>

      <div className="rounded-3xl p-4 text-white shadow-lg" style={{ background: "linear-gradient(135deg,#DC2626,#7F1D1D)" }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center"><Wallet className="w-6 h-6" /></div>
          <div className="flex-1">
            <p className="font-black text-lg leading-tight">أرصدة الزبائن</p>
            <p className="text-[11px] opacity-85">{list.length} زبون</p>
          </div>
          <div className="text-left">
            <p className="text-[10px] opacity-80">مجموع الديون</p>
            <p className="font-black text-xl" dir="ltr" data-testid="sup-balances-total">{fmt(total)}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-3 border-2 border-slate-100 space-y-2">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="sup-balances-search"
            placeholder="ابحث بالاسم، الماركت، المنطقة أو رقم الهاتف..."
            className="w-full pr-9 pl-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-400 focus:outline-none text-sm" />
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setOnlyDebt(true)} data-testid="sup-balances-only-debt"
            className={`text-[11px] font-bold px-3 py-1.5 rounded-full transition-colors ${onlyDebt ? "bg-red-600 text-white" : "bg-slate-100 text-slate-600"}`}>
            عليهم دين
          </button>
          <button type="button" onClick={() => setOnlyDebt(false)} data-testid="sup-balances-all"
            className={`text-[11px] font-bold px-3 py-1.5 rounded-full transition-colors ${!onlyDebt ? "bg-red-600 text-white" : "bg-slate-100 text-slate-600"}`}>
            كل الزبائن
          </button>
          <button type="button" onClick={load} data-testid="sup-balances-refresh"
            className="mr-auto w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
            <RefreshCw className={`w-4 h-4 text-slate-600 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <BalancesTable loading={loading} list={list} />
    </div>
  );
};

const BalancesTable = ({ loading, list }) => {
  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-red-600" /></div>;
  if (list.length === 0) return <div className="py-10 text-center text-slate-400 text-sm bg-white rounded-2xl">لا نتائج</div>;
  return (
    <div className="bg-white rounded-2xl border-2 border-slate-100 overflow-x-auto">
      <table className="w-full text-xs" data-testid="sup-balances-table">
        <thead className="bg-red-50 text-red-800">
          <tr>
            <th className="p-2 text-center w-8">#</th>
            <th className="p-2 text-right">الزبون / الماركت</th>
            <th className="p-2 text-center">الدين الحالي</th>
            <th className="p-2 text-center">آخر تسديد</th>
          </tr>
        </thead>
        <tbody>
          {list.map((c, i) => (
            <tr key={c.id} className="border-t border-slate-100 even:bg-slate-50/60" data-testid={`sup-balance-row-${c.id}`}>
              <td className="p-2 text-center text-slate-400 font-bold">{i + 1}</td>
              <td className="p-2">
                <p className="font-black text-slate-800">{c.full_name || "—"}</p>
                {c.market_name && <p className="text-[10px] text-blue-700 font-bold">🏪 {c.market_name}</p>}
                {c.area && <p className="text-[10px] text-slate-500 break-words">📍 {c.area}</p>}
                {c.phone && <p className="text-[10px] text-slate-500 text-right" dir="ltr">{c.phone}</p>}
              </td>
              <td className="p-2 text-center font-black whitespace-nowrap text-sm" dir="ltr" style={{ color: c.debt > 0 ? "#DC2626" : "#059669" }}>{fmt(c.debt)}</td>
              <td className="p-2 text-center">
                <p className="font-bold text-slate-700" dir="ltr">{fmtDay(c.last_payment_at)}</p>
                {c.last_payment_amount > 0 && <p className="text-[10px] text-emerald-700 font-bold" dir="ltr">{fmt(c.last_payment_amount)}</p>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CustomerBalances;
